<template>
  <PVMultiSelect
    v-model="model"
    :options="options"
    :pt="passThroughProps"
    unstyled
    option-label="text"
    option-value="value"
    scroll-height="19rem"
    @change="onChange"
    @before-show="$emit('before-show')"
    @show="$emit('show')"
    @before-hide="$emit('before-hide')"
    @hide="$emit('hide')"
  >
    <!-- slot for the value, shown in the control when the dropdown is closed -->
    <template
      v-if="$slots.value"
      #value="valueSlotProps"
    >
      <slot
        name="value"
        v-bind="valueSlotProps"
      />
    </template>
    <template #dropdownicon>
      <span class="mimic-bv-caret" />
    </template>
    <template
      v-if="$slots.header"
      #header
    >
      <slot name="header" />
    </template>
    <template #option="optionSlotProps">
      <span class="custom-control-label">
        <span class="d-block text-ellipsis">{{ optionSlotProps.option.text }}</span>
      </span>
    </template>
    <template #empty>
      <li role="presentation">
        <p class="b-dropdown-text">{{ translate({ path: 'VIRTUAL_LIST.NO_RESULTS' }) }}</p>
      </li>
    </template>
  </PVMultiSelect>
</template>
<script lang="ts">
export default {
  name: 'StandardMultiSelect',
  compatConfig: {
    MODE: 3,
  },
};
</script>
<script setup lang="ts" generic="T">
import useTranslation from '@/composables/useTranslation';
import { PassThrough } from '@primevue/core';
import type { MultiSelectPassThroughOptions } from 'primevue/multiselect';
import { computed, onUnmounted } from 'vue';

const model = defineModel<T[]>({ required: true });

const props = withDefaults(
  defineProps<{
    options: { value: T; text: string }[];
    pt?: Record<string, any>;
    size?: 'sm' | 'md' | 'lg';
  }>(),
  {
    size: 'md',
  },
);

defineEmits<{
  (e: 'before-show'): void;
  (e: 'show'): void;
  (e: 'before-hide'): void;
  (e: 'hide'): void;
}>();

const sizeClass = computed(() => {
  if (props.size === 'sm') {
    return 'form-control-sm';
  }

  if (props.size === 'lg') {
    return 'form-control-lg';
  }

  return '';
});

const passThroughProps = computed(
  (): PassThrough<MultiSelectPassThroughOptions> => ({
    root: {
      class: [
        'form-control',
        sizeClass.value,
        'd-inline-flex',
        'align-items-center',
        'w-100', // take parent width (col-sm-6)
        'mw-100', // don't exceed parent
        'cursor-pointer',
      ]
        .filter(Boolean)
        .join(' '),
      style: 'min-width: 0', // needed when parent is a flex item (Bootstrap col)
    },
    labelContainer: {
      class: 'flex-grow-1',
      style: 'min-width: 0', // required for flex truncation
    },
    label: {
      class: 'text-ellipsis text-center',
      style: 'min-width: 0', // required for flex truncation
    },
    dropdown: {
      class: 'flex-shrink-0',
    },
    overlay: {
      class: [
        'show',
        'dropdown-menu',
        'dropdown-menu-compact',
        'py-0',
        'border',
        'rounded',
        'shadow',
        ...(props.pt?.overlay?.class ?? []),
      ]
        .filter(Boolean)
        .join(' '),
      style: 'max-width: 200px',
    },
    listContainer: {
      class: 'scroll-md',
    },
    list: {
      class: 'pl-0 border-0 mb-0',
    },
    option: {
      class: 'dropdown-item custom-control custom-checkbox b-custom-control-sm cursor-pointer px-4pt5 py-1',
    },
    // this should be handled through the header slot
    pcHeaderCheckbox: {
      root: {
        class: 'd-none',
      },
    },
    pcOptionCheckbox: {
      root: {
        class: 'd-none',
      },
      input: {
        class: 'custom-control-input',
      },
      // we handle styling through Bootstrap's classes, so hide the PrimeVue icon
      icon: {
        class: 'd-none',
      },
    },
  }),
);

const { translate } = useTranslation();

let removeSwallow: (() => void) | null = null;

/**
 * Prevents the parent component from closing when the select or select option is clicked.
 */
const swallowParentClickOnce = () => {
  removeSwallow?.();

  const onClick = (event: MouseEvent) => {
    event.stopPropagation();
    removeSwallow?.();
  };

  document.addEventListener('click', onClick, true);
  removeSwallow = () => {
    document.removeEventListener('click', onClick, true);
    removeSwallow = null;
  };
};

const onChange = (event: { originalEvent?: Event }) => {
  // Keyboard select has no follow-up click; don't leave a trap for the next click.
  if (event.originalEvent?.type !== 'mousedown') return;

  swallowParentClickOnce();
};

onUnmounted(() => {
  removeSwallow?.();
});
</script>
